import { type AppDispatch } from 'common/hooks/redux'
import { mergeDefined } from 'common/utils/mergeDefined'
import dayjs from 'dayjs'
import { type IPatientStatus } from 'features/Home/interfaces/IInfoSchedule'
import { hasPendingScheduleMutation } from 'features/Schedule/pusher/schedulePusherEcho'
import { type PatientAppointmentType } from 'features/Schedule/pusher/type'
import _ from 'lodash'
import { scheduleApi } from './schedule.api'
import type { ScheduleAppointmentDto, ScheduleDay, ScheduleWeek } from './schedule.types'

export type PatchResult = { undo: () => void }

export type ScheduleCacheContext = {
  siteId: string
  viewDate: string
  scheduleQueries: Record<string, unknown>
}

type ScheduleDraftScope =
  | { kind: 'day'; date: string }
  | { kind: 'week'; startDate: string; endDate: string }

const appointmentBelongsInScope = (appointmentDate: string, scope: ScheduleDraftScope): boolean => {
  if (scope.kind === 'day') {
    return appointmentDate === scope.date
  }
  return appointmentDate >= scope.startDate && appointmentDate <= scope.endDate
}

const isStrictlyNewer = (incoming?: string | null, current?: string | null): boolean => {
  if (!incoming || !current) return true
  const incomingAt = dayjs(incoming)
  const currentAt = dayjs(current)
  if (!incomingAt.isValid() || !currentAt.isValid()) return true
  return incomingAt.valueOf() > currentAt.valueOf()
}

const mergeDecorationOnly = (
  existing: ScheduleDay['items'][number],
  patientData: PatientAppointmentType,
) => {
  const decoration = _.omit(patientData, ['date', 'start_time', 'end_time', 'room'])
  const merged = {
    ...mergeDefined(
      existing as unknown as Record<string, unknown>,
      decoration as unknown as Record<string, unknown>,
    ),
    room: existing.room,
  } as typeof existing

  if (!isStrictlyNewer(patientData.updated_at, existing.updated_at)) {
    merged.updated_at = existing.updated_at
  }
  return merged
}

const upsertPatientInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  patientData: PatientAppointmentType,
  scope: ScheduleDraftScope,
  allowStructural: boolean,
): boolean => {
  if (!draft?.items) return false

  const index = draft.items.findIndex((item) => item.id === patientData.id)
  const existing = index === -1 ? undefined : draft.items[index]

  const structuralAllowed =
    allowStructural &&
    (!existing ||
      (!hasPendingScheduleMutation(patientData.id) &&
        isStrictlyNewer(patientData.updated_at, existing.updated_at)))

  if (!appointmentBelongsInScope(patientData.date, scope)) {
    if (!existing) return false
    if (structuralAllowed) {
      draft.items.splice(index, 1)
      return true
    }
  }

  if (!existing) {
    if (!allowStructural) return false
    const room = draft.rooms.find((r) => r.value === patientData.room.code)
    draft.items.push({
      ...patientData,
      room,
    })

    if ('has_ehr_booking' in draft && !patientData.emr_updated_at) {
      draft.has_ehr_booking = true
    }

    return true
  }

  const room = draft.rooms.find((r) => r.value === patientData.room.code)
  const newObj = structuralAllowed
    ? ({
        ...mergeDefined(
          existing as unknown as Record<string, unknown>,
          patientData as unknown as Record<string, unknown>,
        ),
        room: room ?? existing.room,
      } as typeof existing)
    : mergeDecorationOnly(existing, patientData)

  if (_.isEqual(existing, newObj)) {
    return false
  }

  draft.items.splice(index, 1, newObj)
  return true
}

const deletePatientInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
): boolean => {
  if (!draft?.items) return false
  const index = draft.items.findIndex((item) => item.id === appointmentId)
  if (index === -1) return false
  draft.items.splice(index, 1)
  return true
}

const updatePatientStatusInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
  status_code: number,
  temperature: number,
): boolean => {
  if (!draft?.items) return false

  const status = draft.patient_statuses.find((s) => s.code === status_code)
  if (!status) return false

  const index = draft.items.findIndex((item) => item.id === appointmentId)
  if (index === -1) return false

  const nextItem = {
    ...draft.items[index],
    status: status as IPatientStatus,
    temperature,
  }

  if (_.isEqual(draft.items[index], nextItem)) {
    return false
  }

  draft.items[index] = nextItem
  return true
}

const updatePatientLocationInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
  room_id: number,
): boolean => {
  if (!draft?.items) return false

  const room = draft.rooms.find((r) => r.id === room_id)
  const index = draft.items.findIndex((item) => item.id === appointmentId)
  if (index === -1 || !room) return false

  const nextItem = { ...draft.items[index], room }
  if (_.isEqual(draft.items[index], nextItem)) {
    return false
  }

  draft.items[index] = nextItem
  return true
}

const updateRoomStatusInDraft = (
  draft: ScheduleDay,
  room_id: number,
  room_status_code: number,
): boolean => {
  if (!draft?.rooms) return false

  const roomIndex = draft.rooms.findIndex((room) => room.id === room_id)
  if (roomIndex === -1) return false

  const status = draft.room_statuses.find((s) => s.code === room_status_code)
  const nextRoom = {
    ...draft.rooms[roomIndex],
    status,
  }

  if (_.isEqual(draft.rooms[roomIndex], nextRoom)) {
    return false
  }

  draft.rooms[roomIndex] = nextRoom
  return true
}

const patchAppointmentInDraft = (
  draft: ScheduleDay | ScheduleWeek,
  appointmentId: number,
  data: Partial<ScheduleAppointmentDto>,
  targetDate?: string,
) => {
  if (!draft?.items) return

  const index = draft.items.findIndex((item) => item.id === appointmentId)
  if (index === -1) return

  const existing = draft.items[index]
  const room = data.room ? draft.rooms.find((r) => r.value === data.room) : existing.room

  const updated = {
    ...existing,
    ...(targetDate && { date: targetDate }),
    ...(data.start_time && { start_time: data.start_time }),
    ...(data.end_time && { end_time: data.end_time }),
    ...(room && { room }),
    ...(data.consultation_staff !== undefined && { consultation_staff: data.consultation_staff }),
    ...(data.temperature_bypass !== undefined && {
      temperature_bypass: data.temperature_bypass,
    }),
    ...(data.title !== undefined && { title: data.title }),
    ...(data.comments !== undefined && { comments: data.comments }),
  }

  if (targetDate && targetDate !== existing.date) {
    draft.items.splice(index, 1)
    draft.items.push(updated)
  } else if (!_.isEqual(existing, updated)) {
    draft.items.splice(index, 1, updated)
  }
}

const applyToScheduleCaches = (
  dispatch: AppDispatch,
  { siteId, viewDate, scheduleQueries }: ScheduleCacheContext,
  mutateDay: (draft: ScheduleDay) => void,
  mutateWeek: (draft: ScheduleWeek, startDate: string, endDate: string) => void,
) => {
  dispatch(
    scheduleApi.util.updateQueryData(
      'fetchScheduleDay',
      { siteId, date: viewDate },
      mutateDay,
    ) as any,
  )

  getActiveWeekQueries(scheduleQueries, siteId, viewDate).forEach(({ startDate, endDate }) => {
    dispatch(
      scheduleApi.util.updateQueryData(
        'fetchScheduleWeek',
        { siteId, startDate, endDate },
        (draft) => mutateWeek(draft, startDate, endDate),
      ) as any,
    )
  })
}

export const applyPatientUpsert = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  patientData: PatientAppointmentType,
  { allowStructural = true }: { allowStructural?: boolean } = {},
) => {
  const { viewDate } = context

  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      upsertPatientInDraft(draft, patientData, { kind: 'day', date: viewDate }, allowStructural)
    },
    (draft, startDate, endDate) => {
      upsertPatientInDraft(
        draft,
        patientData,
        { kind: 'week', startDate, endDate },
        allowStructural,
      )
    },
  )
}

export const applyPatientDelete = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  appointmentId: number,
) => {
  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      deletePatientInDraft(draft, appointmentId)
    },
    (draft) => {
      deletePatientInDraft(draft, appointmentId)
    },
  )
}

export const applyPatientStatusUpdate = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  appointmentId: number,
  status_code: number,
  temperature: number,
) => {
  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      updatePatientStatusInDraft(draft, appointmentId, status_code, temperature)
    },
    (draft) => {
      updatePatientStatusInDraft(draft, appointmentId, status_code, temperature)
    },
  )
}

export const applyPatientLocationUpdate = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  appointmentId: number,
  room_id: number,
) => {
  applyToScheduleCaches(
    dispatch,
    context,
    (draft) => {
      updatePatientLocationInDraft(draft, appointmentId, room_id)
    },
    (draft) => {
      updatePatientLocationInDraft(draft, appointmentId, room_id)
    },
  )
}

export const applyRoomStatusUpdate = (
  dispatch: AppDispatch,
  context: ScheduleCacheContext,
  room_id: number,
  room_status_code: number,
) => {
  const { siteId, viewDate } = context

  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date: viewDate }, (draft) => {
      updateRoomStatusInDraft(draft, room_id, room_status_code)
    }) as any,
  )
}

/** Apply an in-flight appointment change to RTK cache immediately (before API resolves). */
export const patchAppointmentOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  appointmentId: number,
  data: Partial<ScheduleAppointmentDto>,
  getState: () => any,
): PatchResult[] => {
  const patches: PatchResult[] = []
  const scheduleQueries = getState().schedule.queries

  const isDateChanged = (draft: ScheduleDay | ScheduleWeek) => {
    const existing = draft.items?.find((item) => item.id === appointmentId)
    return existing != null && existing.date !== date
  }

  patches.push(
    dispatch(
      scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
        if (!draft?.items) return
        if (isDateChanged(draft)) {
          const index = draft.items.findIndex((item) => item.id === appointmentId)
          if (index !== -1) draft.items.splice(index, 1)
          return
        }
        patchAppointmentInDraft(draft, appointmentId, data, date)
      }) as any,
    ),
  )

  getActiveWeekQueries(scheduleQueries, siteId, date).forEach(({ startDate, endDate }) => {
    patches.push(
      dispatch(
        scheduleApi.util.updateQueryData(
          'fetchScheduleWeek',
          { siteId, startDate, endDate },
          (draft) => {
            patchAppointmentInDraft(draft, appointmentId, data, date)
          },
        ) as any,
      ),
    )
  })

  return patches
}

export const getActiveWeekQueries = (
  queries: any,
  siteId: string,
  date: string,
): Array<{ startDate: string; endDate: string }> => {
  const targetDate = dayjs(date)
  const activeWeekQueries: Array<{ startDate: string; endDate: string }> = []

  Object.keys(queries).forEach((queryKey) => {
    if (queryKey.startsWith('fetchScheduleWeek')) {
      const query = queries[queryKey]
      if (query?.originalArgs?.siteId === siteId) {
        const startDate = dayjs(query.originalArgs.startDate)
        const endDate = dayjs(query.originalArgs.endDate)

        if (targetDate.isSameOrAfter(startDate) && targetDate.isSameOrBefore(endDate)) {
          activeWeekQueries.push({
            startDate: query.originalArgs.startDate,
            endDate: query.originalArgs.endDate,
          })
        }
      }
    }
  })

  return activeWeekQueries
}
